// Core
import { createApp, App as VueApp } from "vue";
import router from "./router";
import App from "./App.vue";

// Libraries
// @ts-ignore
import drag from "v-drag";
import * as Sentry from "@sentry/vue";
import PrimeVue from "primevue/config";
import Aura from "@primeuix/themes/aura";
import { createHead } from "@vueuse/head";
import VueDragscroll from "vue-dragscroll";
import { MotionPlugin } from "@vueuse/motion";
import VueDatePicker from "@vuepic/vue-datepicker";
import { autoAnimatePlugin } from "@formkit/auto-animate/vue";
import { plugin as VueInputAutowidth } from "vue-input-autowidth";
import { onAuthStateChanged, getAuth, signOut } from "firebase/auth";
import { apiMultipartService, apiService } from "./core/services/apiService";

// Services & Helpers
import { initToast } from "@/plugins/toast";
import { vFocus } from "./directives/focus";
import posthogPlugin from "@/plugins/posthog";
import { projectAuth } from "./firebase/config";
import { storageSession } from "@/utils/storage";
import { initInlineSvg } from "@/plugins/inline-svg";
import { initializeAnalytics } from "./core/services/analyticsService";
import { logVersionInfo } from "@/config/version";

// Composables
import { useUser } from "./composables/useUser";
import { useProjectStore } from "./store/project";
import { usePWAUpdate } from "./composables/usePWAUpdate";

// Stores
import pinia from "@/store/pinia";
import { useTeamStore } from "@/store/team";

// Assets
import "nprogress/nprogress.css";
import "@vuepic/vue-datepicker/dist/main.css";
import { UserDetailsAdmin } from "./@types/user";
import { errorHandlerService } from "./core/services/error";

declare global {
  interface Window {
    dataLayer: Record<string, unknown>[];
    fbq: (type: string, event: string) => void;
    clarity: ((set: string, key: string, value: string) => void) & ((event: string, value: string) => void);
  }
}

let app: VueApp | undefined;
let head;

// Sanitize props to avoid serialization issues with reactive proxies, functions, and large objects
const sanitizeProps = (props: any, maxDepth = 3, currentDepth = 0): any => {
  if (currentDepth >= maxDepth || !props) return null;
  
  if (typeof props !== 'object') {
    return typeof props === 'function' ? '[Function]' : props;
  }
  
  if (Array.isArray(props)) {
    return props.slice(0, 10).map(item => sanitizeProps(item, maxDepth, currentDepth + 1));
  }
  
  const sanitized: Record<string, any> = {};
  const keys = Object.keys(props).slice(0, 20); // Limit number of keys
  
  for (const key of keys) {
    try {
      const value = props[key];
      if (value === undefined || value === null) {
        sanitized[key] = value;
      } else if (typeof value === 'function') {
        sanitized[key] = '[Function]';
      } else if (typeof value === 'object') {
        // Skip Vue internal properties and circular references
        if (key.startsWith('_') || key.startsWith('$')) continue;
        sanitized[key] = sanitizeProps(value, maxDepth, currentDepth + 1);
      } else {
        sanitized[key] = value;
      }
    } catch (e) {
      sanitized[key] = '[Error reading property]';
    }
  }
  
  return sanitized;
};

const initializeApp = () => {
  app = createApp(App);
  head = createHead();

  app.config.errorHandler = (error, _vm, info) => {
    console.error("Vue Error:", error, info);
    
    // Verifica se é um erro relacionado a convites/signup
    const currentPath = window.location.pathname;
    const isSignupFlow = currentPath.includes('/sign-up') || window.location.search.includes('inviteId');
    
    // Se for erro em fluxo de signup e for erro de propriedade undefined, não redireciona para 500
    if (isSignupFlow && error instanceof TypeError && error.message.includes('Cannot read')) {
      console.warn("Signup flow error - allowing continuation:", error.message);
      return;
    }
    
    // Capturar erro no Sentry com contexto adicional
    Sentry.withScope((scope) => {
      scope.setTag("error_source", "vue");
      scope.setTag("error_type", "component_error");
      scope.setContext("Vue Error Info", {
        info: info,
        componentName: _vm?.$options?.name || _vm?.$options?.__name || "Unknown Component",
        componentProps: sanitizeProps(_vm?.$props),
        route: window.location.pathname
      });
      
      // Adicionar breadcrumb
      scope.addBreadcrumb({
        category: "vue",
        message: `Component error in ${_vm?.$options?.name || "Unknown"}`,
        level: "error",
        data: { info }
      });
      
      Sentry.captureException(error);
    });
  };

  app.use(pinia);
  app.use(head);
  app.use(router);
  app.use(drag, {});
  app.use(PrimeVue, {
    theme: {
      preset: Aura
    }
  });
  app.use(MotionPlugin);
  app.use(VueDragscroll);
  app.use(posthogPlugin);
  app.use(VueInputAutowidth);
  app.use(autoAnimatePlugin);

  app.component("VueDatePicker", VueDatePicker);

  app.directive("focus", vFocus);

  initInlineSvg(app);
  initToast(app);

  initializeAnalytics();

  // Initialize PWA update detection
  const { initPWAUpdate, cleanup } = usePWAUpdate();
  setTimeout(initPWAUpdate, 2000);

  // Store cleanup function globally for potential cleanup
  (window as any).__pwaUpdateCleanup = cleanup;

  Sentry.init({
    app,
    tracesSampleRate: 1.0,
    replaysOnErrorSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    environment: import.meta.env.MODE || "production",
    integrations: [Sentry.browserTracingIntegration({ router })],
    dsn: import.meta.env.VITE_SENTRY_DSN,
    beforeSend(event, hint) {
      const tags = event.tags || {};
      const error = hint?.originalException;
      
      // Filtrar por nível - só enviar error e warning
      const level = event.level || "error";
      if (level !== "error" && level !== "warning") {
        return null;
      }
      
      // Sempre enviar erros de API
      if (tags.error_source === "api") {
        return event;
      }
      
      // Filtrar erros conhecidos que não são úteis
      const errorMessage = (error as any)?.message || event.exception?.values?.[0]?.value || "";
      const ignoredErrors = [
        "ResizeObserver loop limit exceeded",
        "ResizeObserver loop completed with undelivered notifications",
        "Non-Error promise rejection captured",
        "Network request failed",
        "Failed to fetch",
        "NetworkError when attempting to fetch resource",
        "The operation was aborted",
        "Importing a module script failed"
      ];
      
      if (ignoredErrors.some(ignored => errorMessage.includes(ignored))) {
        return null;
      }
      
      // Adicionar contexto para erros não-API
      if (!tags.error_source) {
        event.tags = {
          ...tags,
          error_source: "client",
          error_type: (error as any)?.name || "UnknownError"
        };
      }
      
      // Adicionar fingerprint para melhor agrupamento
      if (!event.fingerprint) {
        const fileName = event.exception?.values?.[0]?.stacktrace?.frames?.[0]?.filename || "unknown";
        const functionName = event.exception?.values?.[0]?.stacktrace?.frames?.[0]?.function || "unknown";
        event.fingerprint = [
          event.exception?.values?.[0]?.type || "Error",
          fileName.split("/").pop() || "unknown-file",
          functionName
        ];
      }
      
      return event;
    },
    attachStacktrace: true,
    defaultIntegrations: false
  });

  // Log version information
  logVersionInfo();

  app.mount("#app");
};

onAuthStateChanged(projectAuth, async user => {
  if (!app) initializeApp();

  const { setUserStatus } = useUser();

  if (!user) {
    storageSession.clear();

    try {
      await signOut(getAuth());
      setUserStatus();
    } catch (err) {
      errorHandlerService.handleError(err);
    }
    return;
  }

  const token = await user.getIdToken();

  apiService.intercept(token);
  apiMultipartService.intercept(token);

  const { data } = await apiService.get<UserDetailsAdmin>({ endpoint: `users/${user.uid}` });

  if (data) await setUserStatus(data);

  const teamStore = useTeamStore();

  if (!window.location.pathname.includes("f1a3b5c738c9d2f62bac4f37e16d58e9")) {
    await teamStore.fetchTeams();
    await teamStore.migrateTeamProjects();
    teamStore.setPaymentLink(null);

    if (teamStore.isSubscriptionLate && data?.activeTeam?.role === "admin") {
      const subscription = teamStore.subscriptions.find(subscription => subscription.status === "overdue");

      if (subscription) {
        const res = await apiService.get<{ url: string }>({
          endpoint: `subscriptions/${subscription.id}/payment-link`
        });

        if (!res.error && res.data?.url) {
          teamStore.setPaymentLink(res.data.url);
        }
      }
    }

    const projectStore = useProjectStore();

    await projectStore.fetch({ simplified: true, page: 1 });
  }
});
